/**
 * Importing a generated world, with the invented half of it on show.
 *
 * Three exported files go in and a terrain grid comes out. The part that needs
 * an interface is not the upload — it is the **mapping**: a generator names
 * thirteen biomes, the book prints twenty movement terrains, and nothing
 * connects them. The correspondence is this application's invention, it
 * decides every movement rate in the campaign, and an Owner who cannot see it
 * has no way to find out that their tundra is being costed as a mountain.
 *
 * So the table is the surface, the upload is a detail of it, and **the dry run
 * is what the button does by default** — 10,000 cells is not something anybody
 * should have to undo by eye.
 *
 * Every list here comes from the server, like the region editor's: they are
 * the book's, and a stale copy still renders as a confident set of options.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { api, ApiError } from '../api.js';

export interface HeightBand {
  readonly above: number;
  readonly terrain: string;
}

/** The five tables an import is read under, as they were stored. */
export interface StoredMapping {
  readonly biomes?: Readonly<Record<string, string | null>>;
  readonly heightBands?: readonly HeightBand[];
  readonly heightExempt?: readonly string[];
  readonly surroundings?: Readonly<Record<string, string | null>>;
  readonly featureless?: readonly string[];
}

export interface ImportVocabulary {
  readonly imported: {
    readonly source: string | null;
    readonly importedAt: string;
    readonly waterways: number;
    readonly routes: number;
    readonly legend: readonly string[];
    /** The mapping this world was actually imported under. */
    readonly mapping?: StoredMapping;
  } | null;
  readonly biomes: readonly string[];
  readonly defaults: {
    readonly biomes: Readonly<Record<string, string | null>>;
    readonly heightBands: readonly HeightBand[];
    readonly surroundings: Readonly<Record<string, string | null>>;
    readonly heightExempt: readonly string[];
    readonly featureless: readonly string[];
  };
  readonly terrains: readonly string[];
  readonly surroundings: readonly string[];
}

/**
 * What the editor opens on: **the mapping in force, not the defaults**.
 *
 * The panel used to seed every table from `defaults` on each load, including
 * the load that follows a successful import. An Owner who changed four rows,
 * imported, then came back to correct a fifth was shown the defaults again —
 * and re-importing silently reverted their other four, having told them
 * "imported" both times. The five tables decide every movement rate and every
 * lost check in the campaign, so showing something other than what is running
 * is the one thing this surface must not do.
 *
 * Pure, and separate from the component, because a `useEffect` never runs under
 * the static render these tests use.
 */
export function editorState(vocab: ImportVocabulary): {
  biomes: Record<string, string | null>;
  surroundings: Record<string, string | null>;
  bands: readonly HeightBand[];
  exempt: readonly string[];
  featureless: readonly string[];
} {
  // An empty list is a decision — an Owner who ticked nothing exempt — so the
  // fallback is on absence alone.
  const inForce = vocab.imported?.mapping;
  return {
    biomes: { ...(inForce?.biomes ?? vocab.defaults.biomes) },
    surroundings: { ...(inForce?.surroundings ?? vocab.defaults.surroundings) },
    bands: [...(inForce?.heightBands ?? vocab.defaults.heightBands)],
    exempt: [...(inForce?.heightExempt ?? vocab.defaults.heightExempt)],
    featureless: [...(inForce?.featureless ?? vocab.defaults.featureless)],
  };
}

export interface ImportSummary {
  readonly cells: number;
  readonly mapped: number;
  readonly terrains: readonly { readonly terrain: string; readonly share: number }[];
  readonly rivers: number;
  readonly streams: number;
  readonly roads: number;
  readonly trails: number;
  readonly dropped: number;
}

const EMPTY: ImportVocabulary = {
  imported: null,
  biomes: [],
  defaults: { biomes: {}, heightBands: [], surroundings: {}, heightExempt: [], featureless: [] },
  terrains: [],
  surroundings: [],
};

/** The three files, by the part of their name that says which is which. */
const FILES = [
  { key: 'cells', label: 'Cells', hint: 'required — the ground itself' },
  { key: 'rivers', label: 'Rivers', hint: 'optional — what you ford' },
  { key: 'routes', label: 'Routes', hint: 'optional — roads and trails' },
] as const;

/**
 * `compact`'s file trigger, styled rather than left as bare OS chrome.
 *
 * ⚠ **A bare OS file input reads as plain text, not a button.** Tailwind's `file:` variant styles the browser's own
 * pseudo-element rather than replacing the control, so the picker stays
 * native — keyboard, drag-drop, screen readers — while reading as a button
 * like everything else on the page.
 */
const FILE_INPUT_COMPACT =
  'mt-1 block text-xs file:mr-2 file:rounded-md file:border file:border-neutral-300 ' +
  'file:bg-white file:px-2 file:py-1 file:text-xs file:font-medium file:text-neutral-900 ' +
  'hover:file:bg-neutral-50 dark:file:border-neutral-700 dark:file:bg-neutral-900 ' +
  'dark:file:text-neutral-100 dark:hover:file:bg-neutral-800';

type FileKey = (typeof FILES)[number]['key'];

export function WorldImport({
  campaignId,
  onImported,
  compact = false,
}: {
  readonly campaignId: string;
  readonly onImported?: () => void;
  /**
   * The wizard's own rendering, in the app's own type scale rather than
   * this component's bare `h3`/`h4`/`table` markup.
   *
   * ⚠ **A visual mess of fonts, sizes and spacings without it.**
   * `MapSetupPage` is this component's only prior caller, and it
   * carries no stylesheet of its own — every `h3`, `h4`, `label` and `table`
   * here is unstyled, relying on the browser's own defaults, because nothing
   * before the campaign wizard ever put it beside Tailwind-styled prose. The
   * biome/height/surroundings override tables are also fifteen-plus form
   * rows a fresh table almost never needs to touch — the defaults are the
   * book's own best correspondence, and the full editor stays reachable from
   * the map setup page for the Owner who wants to change one.
   */
  readonly compact?: boolean;
}) {
  const [vocab, setVocab] = useState<ImportVocabulary>(EMPTY);
  const [biomes, setBiomes] = useState<Record<string, string | null>>({});
  const [surroundings, setSurroundings] = useState<Record<string, string | null>>({});
  const [bands, setBands] = useState<readonly HeightBand[]>([]);
  const [exempt, setExempt] = useState<readonly string[]>([]);
  const [featureless, setFeatureless] = useState<readonly string[]>([]);
  const [files, setFiles] = useState<Partial<Record<FileKey, unknown>>>({});
  const [names, setNames] = useState<Partial<Record<FileKey, string>>>({});
  const [summary, setSummary] = useState<ImportSummary | null>(null);
  const [status, setStatus] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    void api
      .get<ImportVocabulary>(`/api/campaigns/${campaignId}/world-import`)
      .then((body) => {
        const next = { ...EMPTY, ...body };
        setVocab(next);
        const state = editorState(next);
        setBiomes(state.biomes);
        setSurroundings(state.surroundings);
        setBands(state.bands);
        setExempt(state.exempt);
        setFeatureless(state.featureless);
      })
      .catch(() => setStatus('could not load the import settings'));
  };
  useEffect(load, [campaignId]);

  const read = (key: FileKey, file: File | undefined) => {
    if (!file) return;
    setStatus(null);
    void file
      .text()
      .then((text) => {
        setFiles((current) => ({ ...current, [key]: JSON.parse(text) as unknown }));
        setNames((current) => ({ ...current, [key]: file.name }));
      })
      // Named rather than swallowed: a file that is not JSON is the most
      // likely mistake here, and "nothing happened" is the least useful
      // response to it.
      .catch(() => setStatus(`${file.name} is not readable as JSON`));
  };

  const send = (apply: boolean) => {
    setBusy(true);
    setStatus(null);
    void api
      .post<{ applied: boolean; summary: ImportSummary }>(
        `/api/campaigns/${campaignId}/world-import`,
        {
          cells: files.cells,
          rivers: files.rivers,
          routes: files.routes,
          biomes,
          heightBands: bands,
          heightExempt: exempt,
          surroundings,
          featureless,
          apply,
          source: names.cells ?? 'an imported world',
        },
      )
      .then((body) => {
        setSummary(body.summary);
        setStatus(
          apply ? 'imported — the engine now reads this world' : 'nothing written yet',
        );
        if (apply) {
          load();
          onImported?.();
        }
      })
      .catch((error: unknown) =>
        setStatus(error instanceof ApiError ? error.message : 'the import failed'),
      )
      .finally(() => setBusy(false));
  };

  const ready = Boolean(files.cells);

  if (compact) {
    return (
      <div className="space-y-3">
        {/* ⚠ **Hierarchy, not a hidden toggle.** A first draft collapsed
            Rivers and Routes behind a `<details>` to get to "one button",
            which traded one confusing screen for a control nobody would
            discover. Every picker stays on
            screen; "required" and "optional" plus a visible size difference
            are what tell them apart now. */}
        {/* ⚠ **A `<label>`, not a `<div>` beside the input.** The first draft
            put "Ground data" in a sibling element — visually beside the
            picker, invisible to it: a screen reader announced a nameless
            file control. Wrapping it is what makes the heading the input's
            actual accessible name, the same way every other labelled input
            on this page already works. */}
        <label className="block space-y-1">
          <span className="flex flex-wrap items-baseline gap-2">
            <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
              Ground data
            </span>
            <span className="text-[0.7rem] text-neutral-500 dark:text-neutral-400">
              required — Azgaar calls this &ldquo;Cells&rdquo;
            </span>
          </span>
          <input
            type="file"
            accept=".geojson,.json,application/geo+json,application/json"
            onChange={(event) => read('cells', event.target.files?.[0])}
            className={`block ${FILE_INPUT_COMPACT}`}
          />
          {names.cells && (
            <span className="text-xs text-neutral-600 dark:text-neutral-300">{names.cells}</span>
          )}
        </label>

        <div className="space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="text-xs font-medium text-neutral-600 dark:text-neutral-300">
              Rivers and routes
            </span>
            <span className="text-[0.7rem] text-neutral-500 dark:text-neutral-400">optional</span>
          </div>
          <div className="flex flex-wrap gap-4">
            <label className="text-xs text-neutral-500 dark:text-neutral-400">
              Rivers
              <input
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                onChange={(event) => read('rivers', event.target.files?.[0])}
                className={FILE_INPUT_COMPACT}
              />
              {names.rivers && (
                <span className="text-neutral-600 dark:text-neutral-300">{names.rivers}</span>
              )}
            </label>
            <label className="text-xs text-neutral-500 dark:text-neutral-400">
              Routes
              <input
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                onChange={(event) => read('routes', event.target.files?.[0])}
                className={FILE_INPUT_COMPACT}
              />
              {names.routes && (
                <span className="text-neutral-600 dark:text-neutral-300">{names.routes}</span>
              )}
            </label>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            disabled={!ready || busy}
            onClick={() => send(true)}
            className="rounded-md border border-neutral-300 px-3 py-1 text-sm disabled:opacity-50 dark:border-neutral-700"
          >
            {busy ? 'Importing…' : 'Import'}
          </button>
          {!ready && (
            <span className="text-xs text-neutral-600 dark:text-neutral-300">
              Choose the ground data first.
            </span>
          )}
        </div>
        {status && <p className="text-sm">{status}</p>}
        {summary && (
          <p className="text-xs text-neutral-600 dark:text-neutral-300">
            {summary.cells} cells read, {summary.mapped} mapped — {summary.rivers} rivers,{' '}
            {summary.roads + summary.trails} roads and trails.
          </p>
        )}
        <p className="text-xs text-neutral-500 dark:text-neutral-400">
          Uses this application&rsquo;s default reading of your generator&rsquo;s biomes.{' '}
          <Link to={`/campaigns/${campaignId}/map`} className="underline underline-offset-2">
            Change it on the map setup page
          </Link>{' '}
          if something reads wrong.
        </p>
      </div>
    );
  }

  return (
    <section className="world-import">
      <h3>Import a generated world</h3>
      <p className="hint">
        Export <strong>Cells</strong>, <strong>Rivers</strong> and <strong>Routes</strong> as
        GeoJSON from your map generator. The cells carry the ground; the rivers are what the
        party fords; the routes are what stops them getting lost.
      </p>
      {/*
        **The one thing nothing here can check.** Pins are placed on the world
        map *picture*, and the ground under them is read from this *export* —
        two sets of coordinates that only line up if they cover the same
        ground. An image is pixels: there is no way to tell what extent it
        spans, so a cropped or hand-drawn map reads terrain from the wrong
        place and produces a march that looks entirely reasonable. Only the
        Owner can know, so the requirement is stated where they choose the file.
      */}
      <p className="hint">
        <strong>Use your generator&rsquo;s own image as the world map</strong>, exported at the
        same extent as these files. The party&rsquo;s pins sit on the picture and the ground
        beneath them is read from this export &mdash; a cropped or redrawn map puts the two out
        of register, and nothing can detect that but you.
      </p>

      {vocab.imported ? (
        <p className="imported">
          Currently imported: {vocab.imported.source ?? 'a world'} —{' '}
          {vocab.imported.legend.length} kinds of ground, {vocab.imported.waterways} waterways,{' '}
          {vocab.imported.routes} routes.{' '}
          {vocab.imported.mapping
            ? 'The tables below are the ones it is being read under.'
            : null}
        </p>
      ) : (
        <p className="imported">Nothing imported yet — travel uses whatever regions you declare.</p>
      )}

      <div className="files">
        {FILES.map((file) => (
          <label key={file.key}>
            {file.label} <span className="hint">{file.hint}</span>
            <input
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={(event) => read(file.key, event.target.files?.[0])}
            />
            {names[file.key] ? <span className="chosen">{names[file.key]}</span> : null}
          </label>
        ))}
      </div>

      <h4>How the ground is read</h4>
      <p className="hint">
        <strong>This table is ours, not the ruleset&rsquo;s.</strong> The book prints twenty
        movement terrains and your generator names its own; nothing in either connects them, so
        the correspondence below was invented for this application. Change anything that reads
        wrong to you &mdash; it decides how fast the party crosses the world.
      </p>

      <table className="mapping">
        <thead>
          <tr>
            <th>Biome</th>
            <th>Movement terrain</th>
            <th>Height can&rsquo;t override</th>
          </tr>
        </thead>
        <tbody>
          {vocab.biomes.map((biome) => (
            <tr key={biome}>
              <th scope="row">{biome}</th>
              <td>
                <select
                  value={biomes[biome] ?? ''}
                  onChange={(event) =>
                    setBiomes((current) => ({
                      ...current,
                      [biome]: event.target.value || null,
                    }))
                  }
                >
                  <option value="">water — nothing to walk on</option>
                  {vocab.terrains.map((terrain) => (
                    <option key={terrain} value={terrain}>
                      {terrain}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                {/* A marsh at 400 m is still a marsh; a forest at 2,000 m
                    marches like the mountain it is on. Tick what is decided by
                    what is underfoot rather than by how high it is. */}
                <input
                  type="checkbox"
                  checked={exempt.includes(biome)}
                  onChange={(event) =>
                    setExempt((current) =>
                      event.target.checked
                        ? [...current, biome]
                        : current.filter((name) => name !== biome),
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <h4>Height overrides the biome</h4>
      <p className="hint">
        Generators usually have no mountain biome at all &mdash; elevation is a separate number
        &mdash; so without these bands every mountain range imports as whatever grows on it.
        Read in order, highest first, in metres.
      </p>
      <ul className="bands">
        {bands.map((band, index) => (
          <li key={band.above}>
            above {band.above}m &rarr;{' '}
            <select
              value={band.terrain}
              onChange={(event) =>
                setBands((current) =>
                  current.map((entry, at) =>
                    at === index ? { ...entry, terrain: event.target.value } : entry,
                  ),
                )
              }
            >
              {vocab.terrains.map((terrain) => (
                <option key={terrain} value={terrain}>
                  {terrain}
                </option>
              ))}
            </select>
          </li>
        ))}
      </ul>

      <h4>Getting lost</h4>
      <p className="hint">
        A second list, and a second invention. The ruleset prices movement by one set of
        terrains and the chance of getting lost by a different set of nine, and maps neither
        onto the other. Ground with <strong>nothing to steer by</strong> &mdash; ice, sand,
        tundra &mdash; takes the ruleset&rsquo;s own <em>no landmarks</em> modifier on top of
        its base, which is how a trackless waste ends up harder to cross than a wood without
        anybody inventing a number for it.
      </p>
      <table className="mapping">
        <thead>
          <tr>
            <th>Movement terrain</th>
            <th>Surroundings</th>
            <th>Nothing to steer by</th>
          </tr>
        </thead>
        <tbody>
          {vocab.terrains.map((terrain) => (
            <tr key={terrain}>
              <th scope="row">{terrain}</th>
              <td>
                <select
                  value={surroundings[terrain] ?? ''}
                  onChange={(event) =>
                    setSurroundings((current) => ({
                      ...current,
                      [terrain]: event.target.value || null,
                    }))
                  }
                >
                  <option value="">no check &mdash; nothing comparable</option>
                  {vocab.surroundings.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input
                  type="checkbox"
                  checked={featureless.includes(terrain)}
                  onChange={(event) =>
                    setFeatureless((current) =>
                      event.target.checked
                        ? [...current, terrain]
                        : current.filter((name) => name !== terrain),
                    )
                  }
                />
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="actions">
        <button type="button" disabled={!ready || busy} onClick={() => send(false)}>
          Check it first
        </button>
        <button type="button" disabled={!ready || busy} onClick={() => send(true)}>
          Import and replace
        </button>
      </div>
      {!ready ? <p className="hint">Choose a cells file to begin.</p> : null}
      {status ? <p className="status">{status}</p> : null}

      {summary ? (
        <div className="summary">
          <h4>What that world contains</h4>
          <ul>
            <li>
              {summary.cells} cells read; {summary.mapped} squares of the grid are land
            </li>
            <li>
              {summary.rivers} rivers and {summary.streams} streams to ford
            </li>
            <li>
              {summary.roads} roads and {summary.trails} trails &mdash; a party following one
              makes no check for getting lost
            </li>
            {summary.dropped > 0 ? (
              <li>{summary.dropped} features skipped &mdash; sea routes and anything unusable</li>
            ) : null}
          </ul>
          <ol className="terrains">
            {summary.terrains.map((leg) => (
              <li key={leg.terrain}>
                {leg.terrain} &mdash; {Math.round(leg.share * 100)}%
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </section>
  );
}
